import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { FBoxContainer } from '../../../components/base';
import { treatmentPlanActions } from '../../../store';
import { FORTA_BLUE, FORTA_SUCCESS } from '../../../utils/colors';
import TPExpirationCard from './TPExpirationCard';
import CardLoader from './CardLoader';
import { PayorResponsibilityEnum } from '../../../constants';

import type { RootState } from '../../../store';

function TPExpiringSection() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { tpPriorAuthorizations, tpPriorAuthorizationsLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );

  const filteredTpPriorAuthorizations = useMemo(() => {
    const clientIds = tpPriorAuthorizations.map((pa) => pa.client.id);
    const reauthData = tpPriorAuthorizations.filter(
      (pa) => pa.payor_responsibility === PayorResponsibilityEnum.PRIMARY,
    );
    const clientIdToGetSecondaryPA: number[] = [];
    clientIds.forEach((id) => {
      if (!reauthData.some((rpa) => rpa.client.id === id)) {
        clientIdToGetSecondaryPA.push(id);
      }
    });
    clientIdToGetSecondaryPA.forEach((clientId) => {
      const secondaryPayors = tpPriorAuthorizations.filter(
        (pa) =>
          pa.client.id === clientId &&
          pa.payor_responsibility === PayorResponsibilityEnum.SECONDARY,
      );
      reauthData.push(...secondaryPayors);
    });
    return reauthData;
  }, [tpPriorAuthorizations]);

  useEffect(() => {
    dispatch(
      treatmentPlanActions.getTPPriorAuthorizations({
        getAccessTokenSilently,
        pa_expires_within_days: 90,
      }),
    );
  }, []);

  return (
    <FBoxContainer
      sx={{ flexGrow: 0, minHeight: '35vh', textAlign: 'left', height: 450 }}
      contentStyle={{
        pr: tpPriorAuthorizations?.length > 3 ? 1 : 2,
      }}
    >
      <Typography sx={{ color: FORTA_BLUE.primary, fontSize: 18, fontWeight: 500 }}>
        Upcoming TP re-auth
      </Typography>
      <Box
        sx={{
          overflowY: 'auto',
          height: 360,
          mt: 2.5,
        }}
      >
        {tpPriorAuthorizationsLoading ? (
          Array.from({ length: 3 }).map((_, i) => <CardLoader sx={{ mr: 1 }} key={i} />)
        ) : filteredTpPriorAuthorizations?.length === 0 ? (
          <Box
            sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <CheckCircleIcon sx={{ color: FORTA_SUCCESS }} />
            <Typography ml={0.5}>You&apos;re all caught up!</Typography>
          </Box>
        ) : (
          filteredTpPriorAuthorizations.map((tpPa) => (
            <TPExpirationCard key={tpPa.id} priorAuthorization={tpPa} />
          ))
        )}
      </Box>
    </FBoxContainer>
  );
}

export default TPExpiringSection;
